import { useCallback, useEffect, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { feefoApi, ApiError } from "@/api";
import type { FeefoOpenItem } from "@/types";
import { Spinner, Toast, useToast } from "@/components/ui";
import { FeefoRequestRow } from "./feedbackShared";
import { FeedbackRequestFlow } from "./FeedbackRequestFlow";
import "./feedback.css";

/**
 * Feedback › Open requests — cross-job feed of review invitations sent in the
 * last 30 days that haven't had a response yet. "New feedback request" (from the
 * header) opens the two-step job-picker → confirm-send flow.
 */
export function FeedbackOpenPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [items, setItems] = useState<FeefoOpenItem[] | null>(null);
  const [error, setError] = useState<string | undefined>();
  const [showNew, setShowNew] = useState(false);
  const { toast, showToast } = useToast();

  const load = useCallback(() => {
    let cancelled = false;
    feefoApi
      .listOpenFeedback()
      .then((res) => {
        if (!cancelled) setItems(res.items);
      })
      .catch((err) => {
        if (!cancelled) setError(err instanceof ApiError ? err.message : "Could not load open requests.");
      });
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => load(), [load]);

  // The header's "New feedback request" button routes here with { create: true }.
  useEffect(() => {
    if ((location.state as { create?: boolean } | null)?.create) {
      setShowNew(true);
      navigate(location.pathname, { replace: true, state: null });
    }
  }, [location, navigate]);

  return (
    <section className="feedback-page">
      <header className="feedback-page__head">
        <h1>Open requests</h1>
        <p className="feefo__blurb">
          Review invitations sent in the last 30 days that are still awaiting a response.
        </p>
      </header>

      {error ? (
        <p className="leads__error" role="alert" onClick={() => setError(undefined)}>
          {error}
        </p>
      ) : null}

      {items === null ? (
        <div className="feefo__loading">
          <Spinner label="Loading open requests…" />
        </div>
      ) : items.length === 0 ? (
        <p className="feefo__empty">
          Nothing open. Requests sent in the last 30 days still awaiting a response show up here.
        </p>
      ) : (
        <div className="feefo__list">
          {items.map((r) => (
            <FeefoRequestRow
              key={r.id}
              r={r}
              header={
                <Link to={`/staff/jobs/${r.job_id}`} className="feefo__job-link">
                  <span className="numeric">{r.job_number}</span> · {r.job_name || "Untitled job"}
                </Link>
              }
            />
          ))}
        </div>
      )}

      {showNew ? (
        <FeedbackRequestFlow
          onClose={() => setShowNew(false)}
          onCreated={() => {
            setShowNew(false);
            showToast("Feedback request sent.");
            load();
          }}
        />
      ) : null}

      <Toast toast={toast} />
    </section>
  );
}
